import instance from "../API";
import type {
  CallDownload,
  CallListItem,
  CallMedia,
  CallPageData,
  PreparedCall,
} from "./types";

export async function listPastCalls(cursor?: string | null): Promise<{
  calls: CallListItem[];
  cursor: string | null;
}> {
  const response = await instance.post("/list_past_calls", {
    limit: 20,
    cursor: cursor ?? null,
  });
  return response.data;
}

export async function getCallPage(botId: string): Promise<CallPageData> {
  const response = await instance.post("/get_call_page", { bot_id: botId });
  return response.data;
}

/** One question about this recording. Nothing is stored, and no history is sent. */
export async function askCall(botId: string, question: string): Promise<string> {
  const response = await instance.post("/ask_call", { bot_id: botId, question });
  return response.data.answer as string;
}

/** The brief, meeting summary, action points, subject and screen-share bar. The server dispatches the write-up on the first open: `writeup_ready` is false until it lands. */
export async function prepareCallPage(botId: string): Promise<PreparedCall> {
  const response = await instance.post("/update_call_page", { bot_id: botId });
  return response.data;
}

/** Hide this call from Calls. The recording stays archived. Dooglie staff and org admins. */
export async function deleteCall(botId: string): Promise<void> {
  await instance.post("/delete_call", { bot_id: botId });
}

/** The recruiter's title. Blank clears it and the subject line shows again. */
export async function renameCall(botId: string, title: string): Promise<string> {
  const response = await instance.post("/update_call_page", { bot_id: botId, title });
  return response.data.title as string;
}

/** A fresh playback link, without the transcript. */
export async function getCallMedia(botId: string): Promise<CallMedia> {
  const response = await instance.post("/get_call_page", {
    bot_id: botId,
    media_only: true,
  });
  return response.data.media;
}

/**
 * The call's download zip. `preparing` means the server is building it (the
 * first click on a call, or after the call changed); ask again in a few
 * seconds. `ready` carries a one-hour link that saves the file.
 */
export async function getCallDownload(botId: string): Promise<CallDownload> {
  const response = await instance.post("/get_call_page", { bot_id: botId, download: true });
  return response.data as CallDownload;
}

/** The read-only link to this call. Refused with `{error: "sharing_off"}` until the user turns link sharing on. */
export async function shareCall(botId: string): Promise<{ token: string; expires_at: string }> {
  const response = await instance.post("/update_call_page", { bot_id: botId, action: "share" });
  return response.data;
}

/** Kill every link to this call at once. */
export async function unshareCall(botId: string): Promise<void> {
  await instance.post("/update_call_page", { bot_id: botId, action: "unshare" });
}

/** The user's own link-sharing switch, turned on from the Share dialog. */
export async function setShareLinksEnabled(enabled: boolean): Promise<void> {
  await instance.post("/update_user_state", { share_links_enabled: enabled });
}
